import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils/cn';
import Spinner from '../spinner/spinner';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-[6px] rounded-[8px] font-normal transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed cursor-pointer text-[14px] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 whitespace-nowrap',
  {
    variants: {
      variant: {
        primary:
          'bg-[#0C8DBF] text-white hover:bg-[#0C8DBF]/90 focus-visible:ring-[#0C8DBF] disabled:bg-[#0C8DBF]/50 disabled:hover:bg-[#0C8DBF]/50',
        secondary:
          'bg-[#1F445A] text-white hover:bg-[#1F445A]/90 focus-visible:ring-[#1F445A] disabled:bg-[#1F445A]/50 disabled:hover:bg-[#1F445A]/50',
        outline:
          'border border-gray-300 bg-transparent text-gray-900 hover:bg-gray-100 focus-visible:ring-gray-300 disabled:text-gray-400 disabled:border-gray-200 disabled:hover:bg-transparent',
        ghost:
          'text-gray-700 hover:bg-gray-100 focus-visible:ring-gray-300 disabled:text-gray-400 disabled:hover:bg-transparent',
        destructive:
          'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-600 disabled:bg-red-400 disabled:hover:bg-red-400',
        loading: 'bg-gray-200 text-gray-500 disabled:bg-gray-200 disabled:text-gray-500 gap-[6px] ',
        close:
          'bg-transparent text-[#25151E] hover:bg-[#25151E] hover:text-white focus-visible:ring-[#25151E] disabled:text-gray-400 disabled:hover:bg-transparent',
      },
      size: {
        sm: 'px-3 py-1.5 text-sm',
        md: 'px-4 py-2 text-[14px] font-normal',
        lg: 'px-5 py-2.5 text-lg',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  isLoading?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  loadingText?: string;
  loadingIconClassName?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      isLoading = false,
      disabled,
      children,
      startIcon,
      endIcon,
      loadingText,
      loadingIconClassName,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || isLoading;
    const showText = isLoading && loadingText ? loadingText : children;

    return (
      <button
        onClick={(e) => {
          if (isDisabled) return undefined;
          props?.onClick?.(e);
        }}
        ref={ref}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        className={cn(buttonVariants({ variant: isLoading ? 'loading' : variant, size }), className)}
        {...props}
      >
        {/* Left icon or loader */}
        {isLoading ? (
          <Spinner className={cn(loadingIconClassName, 'border-[2px]')} isLoading={isLoading} />
        ) : (
          startIcon && <span className="inline-flex">{startIcon}</span>
        )}

        {/* Text */}
        <span>{showText}</span>

        {/* Right icon (hidden while loading) */}
        {!isLoading && endIcon && <span className="inline-flex">{endIcon}</span>}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
